import React from "react";
import {UserPermissionEnum} from "../../../utils/types/user";

import {
  CompareDateLabels,
  getAggregationValues,
  hasMorePages,
  ReportProps,
  useServerSort,
} from "./Report.common";
import {
  ReportAdmission,
  ReportAggregationTypes,
  ReportsType,
  SortField,
} from "../../../store/reporting-reports/reporting-reports.common";
import {CardTitle} from "../../../atoms/card-title";
import {RequiredUserPermission} from "../../../atoms/admin-user-permissions/RequiredUserPermission";
import {ReportingContextReports} from "../ReportingContextReports";
import {withCompare} from "./helpers";
import {Aggregation} from "../../../atoms/admin-reporting/atoms/aggregation";
import {LoadMoreButton} from "../atoms/load-more-button";
import {
  Column,
  ListTableReporting,
} from "../../../atoms/admin-reporting/list-table-reporting";
import {
  DateReportsCell,
  IconCheckCell,
  TextCellEllipsis,
} from "../../../atoms/list-table/SharedCells";

export const ARBITRATION_AGREEMENT_REPORT_ID = "arbitration-agreement" as const;

export const ReportArbitrationAgreement = (props: ReportProps) => {
  const [sortDirection, sortField, setSort] = useServerSort(
    SortField.facilityName,
  );

  return (
    <>
      <div className="chart-header">
        <CardTitle title="Arbitration Agreement" subtitle="by Admissions" />
        <CompareDateLabels dateRange={props.dateRange} />
      </div>

      <RequiredUserPermission
        permission={UserPermissionEnum.ARBITRATION_AGREEMENT}
      >
        <ReportingContextReports
          dataPaths={withCompare({
            type: ReportsType.arbitrationAgreement,
            query: {
              from: props.dateRange.from,
              to: props.dateRange.to,
              sortDirection: sortDirection,
              sortField: sortField,
              facilities: props.facilities,
            },
          })}
          enabled
          onDataPathChange={
            props.reportingBlockSetters
              ? props.reportingBlockSetters.setExportDataPath
              : undefined
          }
        >
          {(d, loadMore) => (
            <>
              <div className="aggregation-row">
                <Aggregation
                  reports
                  subject="ACCEPTED"
                  values={getAggregationValues(
                    d,
                    ReportAggregationTypes.positiveAnswersTotal,
                  )}
                />
                <Aggregation
                  reports
                  subject="DECLINED"
                  values={getAggregationValues(
                    d,
                    ReportAggregationTypes.negativeAnswersTotal,
                  )}
                />
              </div>
              <ListTableReporting
                data={d}
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={setSort}
                noRowsMessage="No Admissions found with these parameters..."
                full={props.reportingBlockSetters.isFullReport}
              >
                <Column<ReportAdmission>
                  sortKey={SortField.residentName}
                  cellRenderer={row => (
                    <TextCellEllipsis small>
                      {row.rowData.residentName}
                    </TextCellEllipsis>
                  )}
                  label="Resident Name"
                  width={26}
                />
                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <TextCellEllipsis small>
                      {row.rowData.facilityName}
                    </TextCellEllipsis>
                  )}
                  label="Facility Name"
                  sortKey={SortField.facilityName}
                  width={29}
                />
                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <DateReportsCell data={row.rowData.admissionDate} />
                  )}
                  label="Admission Date"
                  sortKey={SortField.admissionDate}
                  width={14}
                />
                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <DateReportsCell data={row.rowData.answerDate} />
                  )}
                  label="Accepted/Declined date"
                  sortKey={SortField.answerDate}
                  width={22}
                />
                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <IconCheckCell accepted={row.rowData.answerIsPositive} />
                  )}
                  label="Accepted"
                  sortKey={SortField.answerIsPositive}
                  width={10}
                />
                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <IconCheckCell accepted={!row.rowData.answerIsPositive} />
                  )}
                  label="Declined"
                  sortKey={SortField.answerIsNegative}
                  width={10}
                />
              </ListTableReporting>
              <LoadMoreButton
                hasMorePages={hasMorePages(
                  d,
                  props.reportingBlockSetters.isFullReport,
                )}
                onClick={loadMore}
              />
            </>
          )}
        </ReportingContextReports>
      </RequiredUserPermission>
    </>
  );
};
